/*
These are the default Oliver Wyman palette colours (taken from Powerpoint)
Please do not over-write these colours when skinning tools for clients.
Instead create new colour names and add them to the bottom.
You can use the client's logo and the following resources to derive colour values:
    - http://www.palettefx.com/ (image to colours)
    - http://www.colorhexa.com/ (colour variations)
*/

import { map } from 'lodash';

// New RTLS colours from
// https://www.figma.com/design/YnQuUOLQ5uYJZ1wif42Yyp/RTLS-Design-System?node-id=6706-68828&t=O3DZatnGaWXvhjEF-0
// Note this is a work in progress and may not be complete
// Use these colours for new components and updates to existing components

const rtlsColors = {
  white: '#FFFFFF',
  black: '#000000',
  grey10: '#F2F4F8',
  grey20: '#DDE1E6',
  grey30: '#C1C7CD',
  grey40: '#A2A9B0',
  grey50: '#878D96',
  grey60: '#697077',
  grey70: '#4D5358',
  grey80: '#343A3F',
  grey90: '#21272A',
  grey100: '#121619',
  pink10: '#FFF0F7',
  pink20: '#FFD6E8',
  pink30: '#FFAFD2',
  pink40: '#FFAFD2',
  pink50: '#EE5396',
  pink60: '#D02670',
  pink70: '#9F1853',
  pink80: '#740937',
  pink90: '#510224',
  pink100: '#2A0A18',
  red10: '#FFF1F1',
  red20: '#FFD7D9',
  red30: '#FFB3B8',
  red40: '#FF8389',
  red50: '#FA4D56',
  red60: '#DA1E28',
  red70: '#A2191F',
  red80: '#750E13',
  red90: '#520408',
  red100: '#2D0709',
  orange10: '#FFF2E8',
  orange20: '#FDE0CD',
  orange30: '#FFB784',
  orange40: '#FF832B',
  orange50: '#E95F00',
  orange60: '#B74D00',
  orange70: '#8A3800',
  orange80: '#5E2900',
  orange90: '#401B01',
  orange100: '#231000',
  yellow10: '#FCF4D6',
  yellow20: '#FDDC69',
  yellow30: '#F1C21B',
  yellow40: '#D2A106',
  yellow50: '#B28600',
  yellow60: '#8E6A00',
  yellow70: '#684E00',
  yellow80: '#483700',
  yellow90: '#302400',
  yellow100: '#1C1500',
  green10: '#DEFBE6',
  green20: '#A7F0BA',
  green30: '#6FDC8C',
  green40: '#42BE65',
  green50: '#24A148',
  green60: '#198038',
  green70: '#0E6027',
  green80: '#044317',
  green90: '#022D0D',
  green100: '#071908',
  teal10: '#D9FBFB',
  teal20: '#9EF0F0',
  teal30: '#3DDBD9',
  teal40: '#08BDBA',
  teal50: '#009D9A',
  teal60: '#007D79',
  teal70: '#005D5D',
  teal80: '#004144',
  teal90: '#022B30',
  teal100: '#04181A',
  aqua10: '#EDF5FF',
  aqua20: '#BAE6FF',
  aqua30: '#82CFFF',
  aqua40: '#34B3FF',
  aqua50: '#1C94EA',
  aqua60: '#0874C5',
  aqua70: '#03549C',
  aqua80: '#023B6E',
  aqua90: '#012749',
  aqua100: '#061727',
  blue10: '#EDF5FF',
  blue20: '#D0E2FF',
  blue30: '#A6C8FF',
  blue40: '#78A9FF',
  blue50: '#4B8CFF',
  blue60: '#2165FF',
  blue70: '#0B46D3',
  blue80: '#022E9E',
  blue90: '#071E6D',
  blue100: '#001141',
  purple10: '#F6F2FF',
  purple20: '#E8DAFF',
  purple30: '#D4BBFF',
  purple40: '#BE95FF',
  purple50: '#A770FF',
  purple60: '#8C42FF',
  purple70: '#6B2BC5',
  purple80: '#4A1F8D',
  purple90: '#31135E',
  purple100: '#1C0F30',
};

// Legacy colours - TODO review these

const darkSapphire = '#002C77';
const owSapphire = '#008AB3';
const lightSapphire = '#9DE0ED';
const owOnyx = '#5F5F5F';
const lightOnyx = '#BEBEBE';
const veryLightOnyx = '#DFDFDF';
const owTopaz = '#E29815';
const lightTopaz = '#FFCF89';
const owEmerald = '#41A441';
const lightEmerald = '#BDDDA3';
const owIolite = '#646EAC';
const lightIolite = '#C5CAE7';
const owCitrine = '#DD712C';
const lightCitrine = '#FDCFAC';
const owTurquoise = '#079B84';
const lightTurquoise = '#A8DAC9';
const owRuby = '#CB225B';
const lightRuby = '#F8B8BC';
const owWhite = '#FFFFFF';
const owBlack = '#000000';

// assortment specific colours
const assortmentGreyLight = '#F3F3F3';
const assortmentGreyDark = '#808080';
const assortmentBlueLight = '#DDF2FB';
const assortmentBluePrimary = '#2f477c';
const assortmentSecondaryBlue = '#00a0e2';
const assortmentRedPrimary = '#df7d8b';
const assortmentYellowPrimary = '#f3cc92';
const assortmentGreenPrimary = '#6da54d';
const assortmentTealPrimary = '#339087';

// matching colours in webtool/client/style/base/_variable.scss
const assortmentEconomyColour = '#BCC72F';
const assortmentCompetitionColour = '#2CB498';
const assortmentArchitectureColour = '#9C79C8';
const assortmentChartScatter = '#00A0E2';
const assortmentDisabledTextButtonColour = '#CBD1DF';

const assortmentGridLineColor = '#9B9B9B';
const tensionChartPositiveColor = '#6ECADB';
const tensionChartNegativeColor = '#6B8C9F';

// colours in settings pages
const competitorTrafficLightColor = '#00AC92';
const architectureTrafficLightColor = '#9E79C5';
const economicTrafficLightColor = '#BCC743';
const otherTrafficLightColor = '#C2C2C2';
const inactiveTrafficLightColor = '#F0F0F0';

const rulesetDarkBlue = '#29477A';
const scopeHighlightBlue = '#006aa7';
const scopeHighlightRed = '#bf4c4f';

const rulesetBackgroundGrey = '#d2dee6';
const rulesetLightBlue = '#00a0df';

const penaltiesLightGreen = '#68BE4F';
const penaltiesScatterTextColour = '#000';

// used on clustering page for deviation charts and map pins

const clusteringColours = {
  orange: '#df9445',
  green: '#79a43d',
  blue: '#619fcd',
  red: '#bd4633',
  pink: '#e690e3',
  beige: '#eec792',
  darkred: '#8d3736',
  darkgreen: '#707b32',
  lightblue: '#98d7fb',
  gray: '#565656',
  lightgreen: '#c4f081',
  white: '#ffffff',
  black: '#000000',
  lightred: '#e38e7e',
  darkblue: '#3a5e95',
  lightgray: '#a3a3a3',
  cadetblue: '#486370',
  darkpurple: '#b755ab',
};

// used on charts, matches invision design
const chartColor1 = '#E48D50';
const chartColor2 = '#75CBEF';
const chartColor3 = '#9490B7';
const chartColor4 = '#66CC9A';
const chartColor5 = '#575D6C';
const chartColor6 = '#297F98';
const chartColor7 = '#DA8725';
const chartColor8 = '#5AA585';

// used on scatter plot charts
const plotColour1 = '#B3CADF';
const plotColour2 = '#DF9F6F';

const plotLineColour = '#D5D5D5';

const chartFontColor = '#666666';
const columnChartSeriesColor1 = '#7cb5ec';
const columnChartSeriesColor2 = '#434348';

const baseline = '#2c5eA0';
const comparison = '#df9f6f';
const baselinePlotLine = '#9abcd3';
const comparisonPlotLine = '#e4cebc';
const nonMaintainedPlotBand = '#eaf6fc';
const maintainedPlotBand = '#e1e4eb';

// Fonts used on Regions page
const regionStoreCountFontLight = '#ffffff';
const regionStoreCountFontDark = '#181d1f';
// Backgrounds used on Regions page
const regionStoreCountColour1 = '#ffffff';
const regionStoreCountColour2 = '#fdd0a2';
const regionStoreCountColour3 = '#fdae6b';
const regionStoreCountColour4 = '#f16913';
const regionStoreCountColour5 = '#d94801';
const regionStoreCountColour6 = '#b27358';
const regionStoreCountColour7 = '#a96143';
const regionStoreCountColour8 = '#9f502e';
const regionStoreCountColour9 = '#963e19';
const regionStoreCountColour10 = '#8c2d04';

export default {
  rtlsColors,

  darkSapphire,
  owSapphire,
  lightSapphire,
  owOnyx,
  lightOnyx,
  veryLightOnyx,
  owTopaz,
  lightTopaz,
  owEmerald,
  lightEmerald,
  owIolite,
  lightIolite,
  owCitrine,
  lightCitrine,
  owTurquoise,
  lightTurquoise,
  owRuby,
  lightRuby,
  owWhite,
  owBlack,
  assortmentGreyDark,
  assortmentGreyLight,
  assortmentBlueLight,
  assortmentBluePrimary,
  assortmentSecondaryBlue,
  assortmentRedPrimary,
  assortmentYellowPrimary,
  assortmentGreenPrimary,
  assortmentTealPrimary,

  assortmentEconomyColour,
  assortmentCompetitionColour,
  assortmentArchitectureColour,
  assortmentGridLineColor,
  assortmentChartScatter,
  assortmentDisabledTextButtonColour,

  tensionChartPositiveColor,
  tensionChartNegativeColor,

  competitorTrafficLightColor,
  architectureTrafficLightColor,
  economicTrafficLightColor,
  otherTrafficLightColor,
  inactiveTrafficLightColor,

  rulesetDarkBlue,
  scopeHighlightBlue,
  scopeHighlightRed,

  rulesetBackgroundGrey,
  rulesetLightBlue,

  penaltiesLightGreen,
  penaltiesScatterTextColour,

  chartColor1,
  chartColor2,
  chartColor3,
  chartColor4,
  chartColor5,
  chartColor6,
  chartColor7,
  chartColor8,

  orderedChartColors: [...map(clusteringColours, v => v)],

  scatterPlotColours: [plotColour1, plotColour2],

  reports: {
    baseline,
    comparison,
  },

  comparisonWaterfall: {
    chartFontColor,
    distributionBar: plotColour1,
    plotLine: {
      baseline: baselinePlotLine,
      comparison: comparisonPlotLine,
    },
    plotBands: {
      nonMaintained: nonMaintainedPlotBand,
      maintained: maintainedPlotBand,
    },
  },
  statsReport: {
    chartFontColor,
    columnChartSeriesColor1,
    columnChartSeriesColor2,
  },
  plotLineColour,
  clusteringColours,
  regionStoreCountColours: {
    colour: [
      regionStoreCountFontDark,
      regionStoreCountFontDark,
      regionStoreCountFontDark,
      regionStoreCountFontLight,
      regionStoreCountFontLight,
      regionStoreCountFontLight,
      regionStoreCountFontLight,
      regionStoreCountFontLight,
      regionStoreCountFontLight,
      regionStoreCountFontLight,
    ],
    background: [
      regionStoreCountColour1,
      regionStoreCountColour2,
      regionStoreCountColour3,
      regionStoreCountColour4,
      regionStoreCountColour5,
      regionStoreCountColour6,
      regionStoreCountColour7,
      regionStoreCountColour8,
      regionStoreCountColour9,
      regionStoreCountColour10,
    ],
  },
};
